import { BetaBadge } from "@/components/ui/beta-badge";
import { HelpPopover } from "@/components/ui/help-popover";
import { ReactComponent as ReadyToPublishGraphic } from "@/components/ui/icons/ready-to-publish.svg";
import { RevisionChangesList } from "@/data-preparation-tool/inspect-and-publish/revision-changes-list";
import { selectRevisionRegistrationEdges } from "@/data-preparation-tool/store/revision/revision-edge-selectors";
import {
  selectRevisionEntities,
  selectTransformedEntityIds,
} from "@/data-preparation-tool/store/revision/revision-entity-selectors";
import { selectHasRoviScans } from "@/data-preparation-tool/store/revision/revision-scan-selectors";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import {
  appStateSelector,
  store as dataManagementStore,
  ErrorBoundary,
  projectSettingsSelector,
  PublishButton,
  RevisionChangeSummary,
  setPublishSettings,
  SettingGeneratePC,
  SettingGenerateSplats,
  SettingReflection,
  SettingWorkflowStopAfter,
  summarizeRevisionChanges,
  WorkflowUpdateDialog,
} from "@faro-lotv/data-management";
import reflectionsFilterGif from "@faro-lotv/data-management/dist/assets/reflections-filter.gif";
import {
  ExclamationMarkCircleFillIcon,
  FaroButton,
  FaroIconButtonSizes,
  FaroSwitch,
  FaroText,
  FaroTooltip,
  InfoIcon,
  yellow,
} from "@faro-lotv/flat-ui";
import { RevisionStatus, useApiClientContext } from "@faro-lotv/service-wires";
import { DialogProps, Divider } from "@mui/material";
import { Stack } from "@mui/system";
import { JSX, useCallback, useMemo, useState } from "react";

type PublishDraftDialogProps = Pick<DialogProps, "open"> & {
  /** What to do after the publishing has been initiated. */
  afterPublish(): void;
  /** What to do when the user cancels the publishing. */
  onCancel(): void;
  /** Whether the current draft revision has unpublished changes. */
  hasRevisionChanges: boolean;
  /** Setter for whether the Prepare publishing dialog is open. */
  setIsPreparePublishingDialogOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >;
};

/**
 * @returns Dialog to configure and initiate publishing of the draft revision.
 */
export function PublishDraftDialog({
  afterPublish,
  onCancel,
  hasRevisionChanges,
  setIsPreparePublishingDialogOpen,
  ...dialogProps
}: PublishDraftDialogProps): JSX.Element {
  const { generatePC, reflection, generateSplats, workflowStopAfter } =
    useAppSelector(projectSettingsSelector);
  const { isDevModeEnabled } = useAppSelector(appStateSelector);
  const hasRoviScans = useAppSelector(selectHasRoviScans);

  const [settingGeneratePC, setSettingGeneratePC] = useState<SettingGeneratePC>(
    {
      ...generatePC,
    },
  );
  const [settingReflection, setSettingReflection] = useState<SettingReflection>(
    {
      ...reflection,
    },
  );
  const [settingGenerateSplats, setSettingGenerateSplats] =
    useState<SettingGenerateSplats>({
      ...generateSplats,
    });

  // Gaussian splats require Rovi scans, and are only supported with the "enableDev" URL flag.
  const canGenerateSplats = isDevModeEnabled && hasRoviScans;
  const effectiveGenerateSplats = useMemo<SettingGenerateSplats>(
    () => ({
      ...settingGenerateSplats,
      value: canGenerateSplats && settingGenerateSplats.value,
    }),
    [canGenerateSplats, settingGenerateSplats],
  );

  const entities = useAppSelector(selectRevisionEntities);
  const edges = useAppSelector(selectRevisionRegistrationEdges);
  const transformedEntityIds = useAppSelector(selectTransformedEntityIds);

  const revisionChanges = useMemo(() => {
    const edgesChanged = hasRevisionChanges ? edges : [];
    const entitiesChanged = hasRevisionChanges ? entities : [];
    const entitiesTransformed = entitiesChanged.filter(
      (entity) =>
        entity.status === RevisionStatus.modified &&
        transformedEntityIds.includes(entity.id),
    );

    return summarizeRevisionChanges(
      entitiesChanged,
      edgesChanged,
      entitiesTransformed.length ? entitiesTransformed : undefined,
    );
  }, [entities, edges, hasRevisionChanges, transformedEntityIds]);

  const hasPointCloudSettingsChanged =
    settingGeneratePC.value !== generatePC.value ||
    settingReflection.value !== reflection.value;
  const hasPublishSettingsChanged =
    hasPointCloudSettingsChanged ||
    effectiveGenerateSplats.value !== generateSplats.value;

  const ignorePC = useMemo(
    () =>
      shouldIgnorePointCloudGeneration(
        revisionChanges,
        hasPointCloudSettingsChanged,
      ),
    [hasPointCloudSettingsChanged, revisionChanges],
  );

  return (
    <WorkflowUpdateDialog
      variant="action"
      title="Ready to publish"
      description="Select the desired output settings below to publish your data in Sphere XG."
      image={ReadyToPublishGraphic}
      sxImage={{ width: "267px", height: "176px" }}
      content={
        <PublishBody
          hasRoviScans={hasRoviScans}
          settingGeneratePC={settingGeneratePC}
          setSettingGeneratePC={setSettingGeneratePC}
          settingReflection={settingReflection}
          setSettingReflection={setSettingReflection}
          settingGenerateSplats={effectiveGenerateSplats}
          setSettingGenerateSplats={setSettingGenerateSplats}
          revisionChanges={revisionChanges}
        />
      }
      actions={
        <PublishActions
          settingGeneratePC={settingGeneratePC}
          settingReflection={settingReflection}
          settingGenerateSplats={effectiveGenerateSplats}
          workflowStopAfter={workflowStopAfter}
          onCancel={onCancel}
          afterPublish={afterPublish}
          setIsPreparePublishingDialogOpen={setIsPreparePublishingDialogOpen}
          ignorePC={ignorePC}
          isDisabled={!hasRevisionChanges && !hasPublishSettingsChanged}
        />
      }
      {...dialogProps}
    />
  );
}

type PublishBodyProps = {
  /** Whether the project contains Rovi scans. */
  hasRoviScans: boolean;
  /** Settings regarding point cloud generation. */
  settingGeneratePC: SettingGeneratePC;
  /** What to do when the user changes the point cloud generation setting. */
  setSettingGeneratePC(value: SettingGeneratePC): void;
  /** Settings regarding reflection filtering. */
  settingReflection: SettingReflection;
  /** What to do when the user changes the reflection filter setting. */
  setSettingReflection(value: SettingReflection): void;
  /** Settings object for generating Gaussian splats (for Rovi data). */
  settingGenerateSplats: SettingGenerateSplats;
  /** Callback to set new value of settingGenerateSplats. */
  setSettingGenerateSplats(value: SettingGenerateSplats): void;
  /** The changes included in the draft revision. */
  revisionChanges: RevisionChangeSummary;
};

/** @returns Publishing options and change list. */
function PublishBody({
  hasRoviScans,
  settingGeneratePC,
  setSettingGeneratePC,
  settingReflection,
  setSettingReflection,
  settingGenerateSplats,
  setSettingGenerateSplats,
  revisionChanges,
}: PublishBodyProps): JSX.Element {
  const { isDevModeEnabled } = useAppSelector(appStateSelector);

  return (
    <Stack sx={{ gap: 1 }}>
      <FaroText variant="heading12">Live view options</FaroText>
      <FaroSwitch
        fullWidth
        sx={{
          // Prevent the switch from overflowing its container on the right side, which would make
          // a horizontal scrollbar appear at the bottom of the dialog.
          overflow: "hidden",
        }}
        label={
          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
              <FaroText variant="bodyM">3D view</FaroText>
              <FaroTooltip
                enterDelay={0}
                title="Explore your captured environment in an interactive 3D view. When disabled, the published experience includes only the 2D overview and individual captures."
              >
                <InfoIcon sx={{ mb: "-6px" }} />
              </FaroTooltip>
            </Stack>
            <FaroTooltip
              enterDelay={0}
              title="Explore your captured environment in an interactive 3D view. When disabled, the published experience includes only the 2D overview and individual captures."
            >
              <ExclamationMarkCircleFillIcon
                sx={{
                  color: yellow[600],
                  visibility: settingGeneratePC.value ? "hidden" : "visible",
                }}
              />
            </FaroTooltip>
          </Stack>
        }
        inputProps={{ "aria-label": "3D view" }}
        checked={settingGeneratePC.value}
        onToggle={() => {
          const value = !settingGeneratePC.value;
          setSettingGeneratePC({
            ...settingGeneratePC,
            value,
          });

          if (!value) {
            // When disabling point cloud generation, also disable reflection filtering automatically
            setSettingReflection({
              ...settingReflection,
              value: false,
            });
          }
        }}
      />

      <FaroSwitch
        fullWidth
        sx={{
          overflow: "hidden",
        }}
        label={
          <Stack direction="row" sx={{ gap: 1, alignItems: "center", pl: 3 }}>
            <FaroText variant="bodyM">Remove reflective surfaces</FaroText>
            <BetaBadge />
            <HelpPopover
              title="Reflection filter"
              variant="darkInfo"
              interaction="hover"
              description={
                <Stack sx={{ gap: 1 }}>
                  <img
                    src={reflectionsFilterGif}
                    alt="Reflection filtering improves data quality by removing invalid points that are caused by reflective surfaces"
                    style={{ width: "280px", height: "219px" }}
                  />
                  <FaroText variant="bodyM" dark>
                    Enabling the reflection filter may increase processing time.
                    If there are no reflective surfaces (e.g. windows, mirrors),
                    disabling it can save time and resources.
                  </FaroText>
                </Stack>
              }
              size={FaroIconButtonSizes.m}
            />
          </Stack>
        }
        inputProps={{ "aria-label": "reflection filter" }}
        checked={settingReflection.value}
        onToggle={() =>
          setSettingReflection({
            ...settingReflection,
            value: !settingReflection.value,
          })
        }
        disabled={!settingGeneratePC.value}
      />

      {isDevModeEnabled && (
        <FaroSwitch
          fullWidth
          sx={{
            overflow: "hidden",
          }}
          label={
            <Stack>
              <span>Gaussian splats</span>
              <FaroText variant="helpText">Rovi captures only</FaroText>
            </Stack>
          }
          inputProps={{ "aria-label": "gaussian splats" }}
          checked={settingGenerateSplats.value}
          disabled={!hasRoviScans}
          onToggle={() =>
            setSettingGenerateSplats({
              ...settingGenerateSplats,
              value: !settingGenerateSplats.value,
            })
          }
        />
      )}
      {revisionChanges.sum > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <FaroText variant="heading12">Unpublished changes</FaroText>
          <RevisionChangesList revisionChanges={revisionChanges} />
        </>
      )}
    </Stack>
  );
}
type PublishActionsProps = {
  /** Settings regarding point cloud generation. */
  settingGeneratePC: SettingGeneratePC;
  /** Settings regarding reflection filtering. */
  settingReflection: SettingReflection;
  /** Settings object for generating Gaussian splats (for Rovi data). */
  settingGenerateSplats: SettingGenerateSplats;
  /** Where to stop the publishing workflow. */
  workflowStopAfter: SettingWorkflowStopAfter;
  /** What to do when the user cancels the publishing. */
  onCancel(): void;
  /** What to do after the publishing has been initiated. */
  afterPublish(): void;
  /** Setter for whether the Prepare publishing dialog is open. */
  setIsPreparePublishingDialogOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  /** Whether to ignore point cloud generation if only renames are present. */
  ignorePC: boolean;
  /** Whether there are no revision or publish settings changes to publish. */
  isDisabled: boolean;
};

/** @returns Publish and Cancel buttons. */
function PublishActions({
  settingGeneratePC,
  settingReflection,
  settingGenerateSplats,
  workflowStopAfter,
  onCancel,
  afterPublish,
  setIsPreparePublishingDialogOpen,
  ignorePC,
  isDisabled,
}: PublishActionsProps): JSX.Element {
  const { dispatch } = useAppStore();
  const { handleErrorWithToast } = useErrorHandlers();
  const { settingsApiClient, projectApiClient } = useApiClientContext();

  /**
   * Save the settings with a "last writer wins" approach, as specified by PM.
   * Shows an error toast if saving fails.
   */
  const applySettings = useCallback(
    () =>
      setPublishSettings(
        // Needs to be cast because of a subtle difference in type definitions
        // Dispatch is compatible, because the data management slices are included in the app store
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        dispatch as typeof dataManagementStore.dispatch,
        projectApiClient.projectId,
        settingsApiClient,
        handleErrorWithToast,
        {
          generatePC: settingGeneratePC,
          reflection: settingReflection,
          generateSplats: settingGenerateSplats,
        },
      ),
    [
      dispatch,
      handleErrorWithToast,
      projectApiClient.projectId,
      settingGenerateSplats,
      settingGeneratePC,
      settingReflection,
      settingsApiClient,
    ],
  );

  return (
    <>
      <FaroButton variant="ghost" onClick={onCancel}>
        Cancel
      </FaroButton>

      <ErrorBoundary>
        <PublishButton
          generatePC={settingGeneratePC}
          reflection={settingReflection}
          generateSplats={settingGenerateSplats}
          workflowStopAfter={workflowStopAfter}
          caption="Publish"
          isDisabled={isDisabled}
          tooltip={isDisabled ? "No changes to publish" : "Publish the project"}
          onClickBefore={applySettings}
          onClickAfter={afterPublish}
          setIsPreparePublishingDialogOpen={setIsPreparePublishingDialogOpen}
          shouldIgnorePC={ignorePC}
        />
      </ErrorBoundary>
    </>
  );
}

/**
 * @param summary The summary of changes in the draft revision.
 * @param hasPublishSettingsChanged Whether the publish settings have changed.
 * @returns True when point cloud generation can be skipped.
 */
export function shouldIgnorePointCloudGeneration(
  summary: RevisionChangeSummary,
  hasPublishSettingsChanged: boolean,
): boolean {
  const changesDontChangePointCloud =
    summary.capturesAdded === 0 &&
    summary.capturesTransformed === 0 &&
    summary.capturesDeleted === 0 &&
    summary.clustersAdded === 0 &&
    summary.clustersDeleted === 0 &&
    summary.clustersTransformed === 0 &&
    summary.otherChanges === 0;
  return !hasPublishSettingsChanged && changesDontChangePointCloud;
}
